'use client'

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  CheckCircle2,
  Globe2,
  HardDrive,
  MonitorSmartphone,
  Play,
  RefreshCw,
  XCircle,
  type LucideIcon
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Annotation, Annotation2 } from '@/types/annotation'

import { isToolAnnotationRecord, unwrapToolAnnotationResult } from './annotation-result'

const ACTION_LABELS: Record<string, string> = {
  list_approved_roots: 'Approved Folders',
  request_access: 'Grant Folder Access',
  search_entries: 'Search Files',
  inspect_path: 'Inspect Path',
  list_directory: 'List Folder',
  read_text_file: 'Read Text File',
  read_file_metadata: 'Read File Metadata',
  write_text_file: 'Write Text File',
  create_text_file: 'Create Text File',
  create_directory: 'Create Folder',
  apply_text_edits: 'Apply Text Edits',
  extract_file_content: 'Extract File Content',
  extract_document_text: 'Extract Document Text',
  ocr_image_file: 'OCR Image',
  upload_path_as_attachment: 'Upload Attachment',
  upload_file_as_attachment: 'Upload Attachment',
  upload_image_as_attachment: 'Upload Image',
  run_safe_command: 'Run Safe Command',
  duplicate_path: 'Duplicate',
  move_path: 'Move',
  trash_path: 'Move to Trash',
  open_path: 'Open',
  reveal_path: 'Reveal in Finder',
  start_session: 'Start Session',
  advance_session: 'Advance Session',
  capture_screenshot: 'Capture Screenshot',
  computer_control_step: 'Computer Control Step',
  list_devices: 'List Devices',
  handoff_prompt: 'Prompt Handoff',
  handoff_task: 'Browser Handoff',
  handoffTask: 'Browser Handoff',
  get_task_status: 'Browser Status',
  getTaskStatus: 'Browser Status'
}

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short'
})

const MAX_FACTS = 4
const MAX_PREVIEW_ITEMS = 4
const CONTENT_PREVIEW_LENGTH = 320
const BROWSER_SIDECAR_LIVE_POLL_MS = 3500

const DESKTOP_TOOL_META: Record<string, { label: string; icon: LucideIcon }> = {
  DesktopFilesystemTool: {
    label: 'Desktop Files',
    icon: HardDrive
  },
  DesktopHandoffTool: {
    label: 'Desktop Handoff',
    icon: MonitorSmartphone
  },
  DesktopComputerControlTool: {
    label: 'Computer Control',
    icon: MonitorSmartphone
  },
  BrowserExtensionHandoffTool: {
    label: 'Browser Sidecar',
    icon: Globe2
  }
}

type DesktopToolAnnotationProps = {
  annotation: Annotation | Annotation2
  toolKey: string
}

type ResultRecord = Record<string, unknown>

type DetailBadge = {
  label: string
  variant: 'outline' | 'secondary' | 'destructive'
}

type FactField = {
  label: string
  value: string
}

type PathField = {
  label: string
  value: string
}

type PreviewItem = {
  primary: string
  secondary?: string
  meta?: string
}

type PreviewSection = {
  title: string
  items: PreviewItem[]
  remainingCount: number
}

type ScreenshotPreview = {
  url: string
  label: string
  meta: string[]
  context: string[]
}

type PendingActionPreview = {
  title: string
  description: string
  requiresApproval: boolean
  approved: boolean
  stateLabel: string
  details: string[]
  sensitivityReasons: string[]
}

type DecisionPreview = {
  statusKey: 'continue' | 'completed' | 'blocked'
  statusLabel: string
  description: string
  rationale: string
  reasoningSummary: string
  meta: string[]
  badgeVariant: 'secondary' | 'destructive' | 'outline'
}

type ComputerControlStatePreview = {
  title: string
  description: string
  badgeLabel: string
  badgeVariant: 'secondary' | 'destructive' | 'outline'
  containerClassName: string
  detailBadges: string[]
}

type StatusTone = {
  success: boolean
  statusLabel: string
  containerClassName: string
  iconClassName: string
  badgeVariant: 'secondary' | 'destructive' | 'outline'
}

function getToolMeta(toolKey: string) {
  for (const [toolName, metadata] of Object.entries(DESKTOP_TOOL_META)) {
    if (toolKey.startsWith(`${toolName}__`)) {
      return {
        toolName,
        ...metadata
      }
    }
  }

  return null
}

export function isDesktopToolAnnotationKey(toolKey: string) {
  return getToolMeta(toolKey) !== null
}

export function getDesktopToolAnnotationResponseTimeLabel(toolKey: string) {
  return getToolMeta(toolKey)?.label
}

function asRecord(value: unknown): ResultRecord | null {
  return isToolAnnotationRecord(value) ? (value as ResultRecord) : null
}

function getString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function getFiniteNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const numericValue = Number(value)
    if (Number.isFinite(numericValue)) {
      return numericValue
    }
  }

  return null
}

function getArray(value: unknown) {
  return Array.isArray(value) ? value : []
}

function getNestedRecord(record: ResultRecord | null, keys: string[]) {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const candidate = asRecord(record[key])
    if (candidate) {
      return candidate
    }
  }

  return null
}

function getNestedString(record: ResultRecord | null, keys: string[]) {
  if (!record) {
    return ''
  }

  for (const key of keys) {
    const value = getString(record[key])
    if (value) {
      return value
    }
  }

  return ''
}

function normalizeIdentifier(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .trim()
    .toLowerCase()
}

function humanizeIdentifier(value: string) {
  if (!value) {
    return ''
  }

  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase())
}

function truncateText(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength - 1)}…`
}

function formatBytes(value: number) {
  if (value < 1024) {
    return `${value} B`
  }

  const units = ['KB', 'MB', 'GB', 'TB']
  let currentValue = value / 1024
  let unitIndex = 0

  while (currentValue >= 1024 && unitIndex < units.length - 1) {
    currentValue /= 1024
    unitIndex += 1
  }

  return `${currentValue >= 10 ? currentValue.toFixed(0) : currentValue.toFixed(1)} ${units[unitIndex]}`
}

function formatDateTime(value: unknown) {
  const timestamp = getString(value)
  if (!timestamp) {
    return ''
  }

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return timestamp
  }

  return DATE_TIME_FORMATTER.format(date)
}

function getPathLeaf(pathValue: string) {
  const normalizedPath = pathValue.replace(/\/+$/, '')
  if (!normalizedPath) {
    return pathValue
  }

  const segments = normalizedPath.split('/')
  return segments[segments.length - 1] || normalizedPath
}

function shortenId(value: unknown) {
  const id = getString(value)
  if (!id) {
    return ''
  }

  return id.length > 8 ? id.slice(0, 8) : id
}

function getActionKey(toolKey: string, result: ResultRecord | null) {
  const resultAction = normalizeIdentifier(getString(result?.action))
  if (resultAction) {
    return resultAction
  }

  const [, suffix = ''] = toolKey.split('__')
  return normalizeIdentifier(suffix)
}

function getActionLabel(actionKey: string) {
  return ACTION_LABELS[actionKey] || humanizeIdentifier(actionKey)
}

function getCountForAction(result: ResultRecord, actionKey: string) {
  const explicitCount = getFiniteNumber(result.count)
  if (explicitCount !== null) {
    return explicitCount
  }

  if (actionKey === 'list_approved_roots' || actionKey === 'request_access') {
    return getArray(result.approvedRoots).length
  }

  return null
}

function getSessionRecord(result: ResultRecord | null) {
  return getNestedRecord(result, ['session'])
}

function getLatestScreenshotRecord(result: ResultRecord | null) {
  return getNestedRecord(result, [
    'latestScreenshot',
    'latest_screenshot',
    'latestSnapshot',
    'latest_snapshot',
    'finalSnapshot',
    'final_snapshot',
    'screenshot',
    'snapshot'
  ])
}

function getPendingActionRecord(result: ResultRecord | null) {
  return getNestedRecord(result, ['pendingAction', 'pending_action', 'action', 'proposedAction'])
}

function getLatestDecisionRecord(result: ResultRecord | null) {
  return getNestedRecord(result, ['latestDecision', 'latest_decision', 'decision'])
}

function getEventRecords(result: ResultRecord | null) {
  return getArray(result?.events)
    .map((value) => asRecord(value))
    .filter((value): value is ResultRecord => value !== null)
}

function getSessionStatus(result: ResultRecord | null) {
  return getNestedString(getSessionRecord(result), ['status']) || getString(result?.status)
}

function getResultSessionId(result: ResultRecord | null) {
  return (
    getString(result?.sessionId || result?.session_id) ||
    getString(getSessionRecord(result)?.id)
  )
}

function isTerminalSessionStatus(status: string) {
  const normalized = normalizeIdentifier(status)

  return normalized === 'completed' || normalized === 'failed' || normalized === 'cancelled'
}

function mergeLiveSessionResult(
  initialResult: ResultRecord | null,
  liveResult: ResultRecord | null
): ResultRecord | null {
  if (!initialResult || !liveResult) {
    return initialResult || liveResult
  }

  const liveSession = getSessionRecord(liveResult)
  const liveStatus = getString(liveSession?.status) || getString(liveResult.status)

  return {
    ...initialResult,
    ...liveResult,
    action: getString(initialResult.action) || getString(liveResult.action),
    command: initialResult.command || liveResult.command,
    status: liveStatus || getString(initialResult.status),
    success: liveStatus
      ? !['failed', 'cancelled'].includes(normalizeIdentifier(liveStatus))
      : initialResult.success
  }
}

function getBrowserSnapshotRecord(result: ResultRecord | null) {
  const directSnapshot = getNestedRecord(result, [
    'latestSnapshot',
    'latest_snapshot',
    'finalSnapshot',
    'final_snapshot',
    'browserSnapshot',
    'browser_snapshot'
  ])
  if (getString(directSnapshot?.url) || getString(directSnapshot?.title)) {
    return directSnapshot
  }

  const screenshot = getLatestScreenshotRecord(result)
  const metadata = asRecord(screenshot?.metadata)

  return asRecord(metadata?.browser_snapshot)
}

function isComputerControlResult(toolName: string, result: ResultRecord | null) {
  if (toolName === 'BrowserExtensionHandoffTool') {
    return false
  }

  return (
    toolName === 'DesktopComputerControlTool' ||
    getString(result?.mode) === 'screenshot_first' ||
    !!getSessionRecord(result) ||
    !!getPendingActionRecord(result) ||
    !!getLatestDecisionRecord(result)
  )
}

function getEventCount(result: ResultRecord | null) {
  return getEventRecords(result).length
}

function getPendingActionSummary(result: ResultRecord | null) {
  const pendingAction = getPendingActionRecord(result)
  if (!pendingAction) {
    return ''
  }

  return (
    getString(pendingAction.summary) ||
    getString(pendingAction.description) ||
    getString(pendingAction.reason) ||
    getString(pendingAction.rationale) ||
    humanizeIdentifier(getString(pendingAction.kind) || getString(pendingAction.type))
  )
}

function getCountLabel(actionKey: string, count: number) {
  switch (actionKey) {
    case 'list_devices':
      return `${count} device${count === 1 ? '' : 's'}`
    case 'search_entries':
      return `${count} match${count === 1 ? '' : 'es'}`
    case 'list_directory':
      return `${count} item${count === 1 ? '' : 's'}`
    case 'list_approved_roots':
    case 'request_access':
      return `${count} folder${count === 1 ? '' : 's'}`
    default:
      return `${count} result${count === 1 ? '' : 's'}`
  }
}

function buildSummary(
  result: ResultRecord,
  actionKey: string,
  toolLabel: string,
  toolName: string
) {
  const message = getString(result.message)
  if (message) {
    return message
  }

  if (isComputerControlResult(toolName, result)) {
    const sessionStatus = getSessionStatus(result)
    const eventCount = getEventCount(result)
    const screenshot = getLatestScreenshotRecord(result)
    const screenshotCapturedAt = formatDateTime(
      screenshot?.capturedAt ||
        screenshot?.captured_at ||
        screenshot?.createdAt ||
        screenshot?.created_at
    )
    const screenshotApp =
      getString(screenshot?.activeAppName) || getString(screenshot?.active_app_name)
    const pendingActionSummary = getPendingActionSummary(result)
    const pendingAction = buildPendingActionPreview(result)
    const latestDecision = buildDecisionPreview(result)
    const normalizedSessionStatus = normalizeIdentifier(sessionStatus)
    const awaitingApproval =
      result.awaitingApproval === true ||
      normalizedSessionStatus === 'awaiting_user_approval' ||
      !!(pendingAction?.requiresApproval && !pendingAction.approved)

    if (awaitingApproval && pendingAction) {
      return `Waiting for approval before ${pendingAction.title.toLowerCase()}.`
    }

    if (latestDecision?.statusKey === 'blocked') {
      return latestDecision.description
        ? `Planner is blocked: ${latestDecision.description}`
        : 'Planner is blocked and needs help before it can continue.'
    }

    if (latestDecision?.statusKey === 'completed') {
      return latestDecision.description
        ? `Planner marked this goal complete. ${latestDecision.description}`
        : 'Planner marked this desktop goal complete.'
    }

    if (pendingAction && (result.planned === true || result.requiresHumanAction === true)) {
      return `Planned next action: ${pendingAction.title}.`
    }

    if (pendingActionSummary && sessionStatus === 'awaiting_user_approval') {
      return `Awaiting approval for: ${pendingActionSummary}.`
    }

    if (screenshotApp && screenshotCapturedAt) {
      return `Running with the latest screenshot from ${screenshotApp}, captured ${screenshotCapturedAt}.`
    }

    if (eventCount > 0 && screenshotCapturedAt) {
      return `${eventCount} trace event${eventCount === 1 ? '' : 's'} recorded. Latest screenshot captured ${screenshotCapturedAt}.`
    }

    if (eventCount > 0) {
      return `${eventCount} trace event${eventCount === 1 ? '' : 's'} recorded for this desktop control step.`
    }

    if (normalizedSessionStatus === 'created') {
      return 'Desktop control session created and waiting for the first screenshot.'
    }

    if (normalizedSessionStatus === 'awaiting_desktop') {
      return 'Desktop control session is waiting for the desktop device to respond.'
    }

    if (sessionStatus) {
      return `Desktop control session is ${humanizeIdentifier(sessionStatus).toLowerCase()}.`
    }
  }

  switch (actionKey) {
    case 'list_devices': {
      const count = getCountForAction(result, actionKey) ?? 0
      return count > 0
        ? `${count} desktop device${count === 1 ? '' : 's'} available.`
        : 'No trusted desktop devices were found.'
    }
    case 'list_approved_roots': {
      const count = getCountForAction(result, actionKey) ?? 0
      return count > 0
        ? `${count} approved folder${count === 1 ? '' : 's'} available.`
        : 'No folders have been approved yet.'
    }
    case 'request_access':
      return getString(result.approvedRoot)
        ? 'Folder access granted for desktop filesystem actions.'
        : 'Folder access request completed.'
    case 'search_entries': {
      const count = getCountForAction(result, actionKey) ?? 0
      const searchMode = getString(result.searchMode)
      const modeSuffix = searchMode ? ` in ${searchMode} search` : ''
      return count > 0
        ? `${count} match${count === 1 ? '' : 'es'} found${modeSuffix}.`
        : `No matches found${modeSuffix}.`
    }
    case 'inspect_path': {
      const entry = asRecord(result.entry)
      if (entry?.isDirectory === true) {
        return 'Folder details loaded.'
      }
      if (entry?.isFile === true) {
        return 'File details loaded.'
      }
      return 'Path details loaded.'
    }
    case 'list_directory': {
      const count = getCountForAction(result, actionKey) ?? 0
      return count > 0 ? `${count} item${count === 1 ? '' : 's'} listed.` : 'This folder is empty.'
    }
    case 'read_text_file': {
      const pathValue = getString(result.path)
      const size = getFiniteNumber(result.size)
      if (pathValue && size !== null) {
        return `Read ${formatBytes(size)} from ${getPathLeaf(pathValue)}.`
      }
      if (pathValue) {
        return `Read ${getPathLeaf(pathValue)}.`
      }
      return 'Text file loaded.'
    }
    case 'read_file_metadata': {
      const pathValue = getString(result.path)
      return pathValue ? `Metadata loaded for ${getPathLeaf(pathValue)}.` : 'File metadata loaded.'
    }
    case 'extract_file_content':
    case 'extract_document_text': {
      const pathValue = getString(result.path)
      const format = getString(result.format)
      const pathLabel = pathValue ? getPathLeaf(pathValue) : format || 'file'
      return `Extracted readable content from ${pathLabel}.`
    }
    case 'ocr_image_file': {
      const pathValue = getString(result.path)
      return pathValue ? `OCR completed for ${getPathLeaf(pathValue)}.` : 'OCR completed.'
    }
    case 'upload_path_as_attachment':
    case 'upload_file_as_attachment':
    case 'upload_image_as_attachment': {
      const count = getFiniteNumber(result.count) ?? getArray(result.attachments).length
      const pathValue = getString(result.path)
      if (pathValue && count > 0) {
        return `Uploaded ${getPathLeaf(pathValue)} as a chat attachment.`
      }
      return count > 0
        ? `Uploaded ${count} attachment${count === 1 ? '' : 's'}.`
        : 'Attachment upload completed.'
    }
    case 'run_safe_command': {
      const commandName = getString(result.command)
      const exitCode = getFiniteNumber(result.exitCode)
      if (commandName && exitCode !== null) {
        return exitCode === 0
          ? `${commandName} completed successfully.`
          : `${commandName} exited with code ${exitCode}.`
      }
      return 'Safe terminal command completed.'
    }
    case 'write_text_file':
    case 'create_text_file': {
      const pathValue = getString(result.path)
      return pathValue ? `Saved ${getPathLeaf(pathValue)}.` : 'File saved.'
    }
    case 'create_directory': {
      const pathValue = getString(result.path)
      return pathValue ? `Created ${getPathLeaf(pathValue)}.` : 'Folder created.'
    }
    case 'apply_text_edits': {
      const editsApplied = getFiniteNumber(result.editsApplied) ?? 0
      return editsApplied > 0
        ? `${editsApplied} text edit${editsApplied === 1 ? '' : 's'} applied.`
        : 'File updated.'
    }
    case 'duplicate_path':
      return 'Duplicate created.'
    case 'move_path':
      return result.unchanged === true ? 'Source and destination are the same.' : 'Move completed.'
    case 'trash_path':
      return 'Moved to Trash.'
    case 'open_path':
      return 'Opened on desktop.'
    case 'reveal_path':
      return 'Revealed in Finder.'
    default:
      return `${toolLabel} action completed.`
  }
}

function buildDetailBadges(result: ResultRecord, actionKey: string) {
  const badges: DetailBadge[] = []
  const count = getCountForAction(result, actionKey)
  const searchMode = getString(result.searchMode)
  const size = getFiniteNumber(result.size)
  const editsApplied = getFiniteNumber(result.editsApplied)
  const exitCode = getFiniteNumber(result.exitCode)
  const format = getString(result.format)
  const strategy = getString(result.strategy)
  const command = asRecord(result.command)
  const commandStatus = getString(command?.status)
  const session = getSessionRecord(result)
  const sessionStatus = getString(session?.status)
  const eventCount = getEventCount(result)
  const phase = getFiniteNumber(result.phase)
  const mode = getString(result.mode)
  const operation = getString(result.operation)
  const pendingAction = getPendingActionRecord(result)
  const latestDecision = getLatestDecisionRecord(result)
  const decisionStatus = normalizeIdentifier(getString(latestDecision?.status))

  if (count !== null) {
    badges.push({
      label: getCountLabel(actionKey, count),
      variant: 'outline'
    })
  }

  if (searchMode) {
    badges.push({
      label: `${humanizeIdentifier(searchMode)} Search`,
      variant: 'outline'
    })
  }

  if (size !== null && size > 0) {
    badges.push({
      label: formatBytes(size),
      variant: 'outline'
    })
  }

  if (editsApplied !== null && editsApplied > 0) {
    badges.push({
      label: `${editsApplied} Edit${editsApplied === 1 ? '' : 's'}`,
      variant: 'secondary'
    })
  }

  if (format) {
    badges.push({
      label: format.toUpperCase(),
      variant: 'outline'
    })
  }

  if (strategy) {
    badges.push({
      label: humanizeIdentifier(strategy),
      variant: 'outline'
    })
  }

  if (exitCode !== null) {
    badges.push({
      label: `Exit ${exitCode}`,
      variant: exitCode === 0 ? 'secondary' : 'destructive'
    })
  }

  if (result.permissionRequired === true) {
    badges.push({
      label: 'Permission Required',
      variant: 'destructive'
    })
  }

  if (result.created === true) {
    badges.push({
      label: 'Created',
      variant: 'secondary'
    })
  }

  if (result.overwritten === true) {
    badges.push({
      label: 'Overwritten',
      variant: 'secondary'
    })
  }

  if (result.unchanged === true) {
    badges.push({
      label: 'Unchanged',
      variant: 'secondary'
    })
  }

  if (result.trashed === true) {
    badges.push({
      label: 'Trashed',
      variant: 'secondary'
    })
  }

  if (result.opened === true) {
    badges.push({
      label: 'Opened',
      variant: 'secondary'
    })
  }

  if (result.revealed === true) {
    badges.push({
      label: 'Revealed',
      variant: 'secondary'
    })
  }

  if (commandStatus) {
    badges.push({
      label: humanizeIdentifier(commandStatus),
      variant: 'outline'
    })
  }

  if (sessionStatus) {
    const normalized = normalizeIdentifier(sessionStatus)
    badges.push({
      label: humanizeIdentifier(sessionStatus),
      variant:
        normalized === 'failed' || normalized === 'cancelled'
          ? 'destructive'
          : normalized === 'completed'
            ? 'secondary'
            : 'outline'
    })
  }

  if (eventCount > 0) {
    badges.push({
      label: `${eventCount} Event${eventCount === 1 ? '' : 's'}`,
      variant: 'outline'
    })
  }

  if (phase !== null) {
    badges.push({
      label: `Phase ${phase}`,
      variant: 'outline'
    })
  }

  if (mode) {
    badges.push({
      label: humanizeIdentifier(mode),
      variant: 'outline'
    })
  }

  if (operation) {
    badges.push({
      label: humanizeIdentifier(operation),
      variant: 'outline'
    })
  }

  if (result.planned === true) {
    badges.push({
      label: 'Planned',
      variant: 'outline'
    })
  }

  if (result.awaitingApproval === true) {
    badges.push({
      label: 'Awaiting Approval',
      variant: 'outline'
    })
  }

  if (result.canAdvance === true) {
    badges.push({
      label: 'Can Advance',
      variant: 'secondary'
    })
  }

  if (result.requiresHumanAction === true) {
    badges.push({
      label: 'Human Action',
      variant: 'outline'
    })
  }

  if (decisionStatus) {
    badges.push({
      label: `Decision: ${humanizeIdentifier(decisionStatus)}`,
      variant:
        decisionStatus === 'blocked'
          ? 'destructive'
          : decisionStatus === 'completed'
            ? 'secondary'
            : 'outline'
    })
  }

  if (pendingAction) {
    badges.push({
      label: result.pendingActionApproved === true ? 'Action Approved' : 'Pending Action',
      variant: result.pendingActionApproved === true ? 'secondary' : 'outline'
    })
  }

  return badges
}

function pushPathField(
  pathFields: PathField[],
  seenPaths: Set<string>,
  label: string,
  value: unknown
) {
  const pathValue = getString(value)
  if (!pathValue) {
    return
  }

  const dedupeKey = `${label}:${pathValue}`
  if (seenPaths.has(dedupeKey)) {
    return
  }

  seenPaths.add(dedupeKey)
  pathFields.push({
    label,
    value: pathValue
  })
}

function buildPathFields(result: ResultRecord) {
  const entry = asRecord(result.entry)
  const pathFields: PathField[] = []
  const seenPaths = new Set<string>()

  pushPathField(pathFields, seenPaths, 'Path', entry?.path)
  pushPathField(pathFields, seenPaths, 'Path', result.path)
  pushPathField(pathFields, seenPaths, 'Folder', result.directory)
  pushPathField(pathFields, seenPaths, 'Source', result.sourcePath)
  pushPathField(pathFields, seenPaths, 'Destination', result.destinationPath)
  pushPathField(pathFields, seenPaths, 'Working Directory', result.workingDirectory)
  pushPathField(pathFields, seenPaths, 'Approved Root', result.approvedRoot)

  return pathFields
}

function pushFactField(factFields: FactField[], label: string, value: string) {
  if (!value || factFields.length >= MAX_FACTS) {
    return
  }

  factFields.push({
    label,
    value
  })
}

function buildFactFields(result: ResultRecord, toolName: string) {
  const command = asRecord(result.command)
  const entry = asRecord(result.entry)
  const session = getSessionRecord(result)
  const latestScreenshot = getLatestScreenshotRecord(result)
  const pendingAction = getPendingActionRecord(result)
  const latestDecision = getLatestDecisionRecord(result)
  const factFields: FactField[] = []

  if (isComputerControlResult(toolName, result)) {
    pushFactField(factFields, 'Session', shortenId(result.sessionId || session?.id))
    pushFactField(factFields, 'Goal', getString(session?.goal))
    pushFactField(
      factFields,
      'Next Action',
      buildPendingActionPreview(result)?.title || getPendingActionSummary(result)
    )
    pushFactField(factFields, 'Decision', humanizeIdentifier(getString(latestDecision?.status)))
    pushFactField(
      factFields,
      'Screenshot',
      formatDateTime(
        latestScreenshot?.capturedAt ||
          latestScreenshot?.captured_at ||
          latestScreenshot?.createdAt ||
          latestScreenshot?.created_at
      )
    )
    pushFactField(
      factFields,
      'Advance',
      result.canAdvance === true ? 'Ready' : result.canAdvance === false ? 'Blocked' : ''
    )
    pushFactField(factFields, 'Human Input', result.requiresHumanAction === true ? 'Required' : '')

    return factFields
  }

  pushFactField(factFields, 'Query', getString(result.query))
  pushFactField(factFields, 'Type', getString(entry?.kind))
  pushFactField(factFields, 'Format', getString(result.format))
  pushFactField(factFields, 'Strategy', humanizeIdentifier(getString(result.strategy)))
  pushFactField(factFields, 'Session', shortenId(result.sessionId || session?.id))
  pushFactField(factFields, 'Operation', humanizeIdentifier(getString(result.operation)))
  pushFactField(factFields, 'Goal', getString(session?.goal))
  pushFactField(factFields, 'Next Action', getPendingActionSummary(result))
  pushFactField(
    factFields,
    'Screenshot',
    formatDateTime(
      latestScreenshot?.capturedAt ||
        latestScreenshot?.captured_at ||
        latestScreenshot?.createdAt ||
        latestScreenshot?.created_at
    )
  )
  const exitCode = getFiniteNumber(result.exitCode)
  if (exitCode !== null) {
    pushFactField(factFields, 'Exit Code', String(exitCode))
  }
  pushFactField(factFields, 'Modified', formatDateTime(entry?.modifiedAt))
  pushFactField(factFields, 'Created', formatDateTime(entry?.createdAt))
  pushFactField(factFields, 'Device', shortenId(command?.device_id))
  pushFactField(
    factFields,
    'Action Type',
    humanizeIdentifier(getString(pendingAction?.kind) || getString(pendingAction?.type))
  )

  if (factFields.length < MAX_FACTS) {
    const completedAt = formatDateTime(command?.completed_at)
    pushFactField(factFields, 'Completed', completedAt)
  }

  return factFields
}

function buildEntryPreviewItem(value: unknown) {
  const entry = asRecord(value)
  if (!entry) {
    return null
  }

  const pathValue = getString(entry.path)
  const primary = getString(entry.name) || pathValue
  if (!primary) {
    return null
  }

  const secondaryParts: string[] = []
  const kind = getString(entry.kind)
  const parentPath = getString(entry.parentPath)
  const size = getFiniteNumber(entry.size)

  if (kind) {
    secondaryParts.push(kind)
  }

  if (size !== null && size > 0 && entry.isDirectory !== true) {
    secondaryParts.push(formatBytes(size))
  }

  if (parentPath) {
    secondaryParts.push(parentPath)
  }

  return {
    primary,
    secondary: secondaryParts.join(' • '),
    meta: entry.requiresApproval === true ? 'Needs Access' : undefined
  }
}

function buildRootPreviewItem(value: unknown) {
  const root = asRecord(value)
  if (!root) {
    return null
  }

  const pathValue = getString(root.path)
  const primary = getString(root.name) || pathValue
  if (!primary) {
    return null
  }

  return {
    primary,
    secondary: pathValue && pathValue !== primary ? pathValue : undefined
  }
}

function buildDevicePreviewItem(value: unknown) {
  const device = asRecord(value)
  if (!device) {
    return null
  }

  const metadata = asRecord(device.metadata)
  const browser = getString(metadata?.browser)
  const primary = getString(device.name) || 'Desktop device'
  const secondaryParts = [
    browser ? humanizeIdentifier(browser) : '',
    getString(device.platform),
    getString(device.app_version) ? `v${getString(device.app_version)}` : '',
    formatDateTime(device.last_seen_at)
  ].filter(Boolean)

  return {
    primary,
    secondary: secondaryParts.join(' • '),
    meta: shortenId(device.id)
  }
}

function buildEditPreviewItem(value: unknown) {
  const edit = asRecord(value)
  if (!edit) {
    return null
  }

  const index = getFiniteNumber(edit.index)
  const occurrencesReplaced = getFiniteNumber(edit.occurrencesReplaced)

  return {
    primary: `Edit ${index !== null ? index + 1 : ''}`.trim(),
    secondary:
      occurrencesReplaced !== null
        ? `${occurrencesReplaced} replacement${occurrencesReplaced === 1 ? '' : 's'}`
        : undefined
  }
}

function buildAttachmentPreviewItem(value: unknown) {
  const attachment = asRecord(value)
  if (!attachment) {
    return null
  }

  const primary = getString(attachment.name) || getString(attachment.path)
  if (!primary) {
    return null
  }

  const size = getFiniteNumber(attachment.size)

  const secondaryParts = [
    getString(attachment.kind),
    getString(attachment.mimeType || attachment.type),
    size !== null ? formatBytes(size) : ''
  ].filter(Boolean)

  return {
    primary,
    secondary: secondaryParts.join(' • '),
    meta: shortenId(attachment.id)
  }
}

function buildEventPreviewItem(value: unknown) {
  const event = asRecord(value)
  if (!event) {
    return null
  }

  const summary =
    getString(event.summary) || humanizeIdentifier(getString(event.type)) || 'Desktop event'
  const secondaryParts = [
    humanizeIdentifier(getString(event.status)),
    formatDateTime(event.created_at)
  ].filter(Boolean)
  const sequence = getFiniteNumber(event.sequence)

  return {
    primary: summary,
    secondary: secondaryParts.join(' • '),
    meta: sequence !== null ? `#${sequence}` : undefined
  }
}

function buildBrowserSidecarEventEvidence(value: unknown): string[] {
  const event = asRecord(value)
  const payload = asRecord(event?.payload_json || event?.payloadJson)
  const action = asRecord(payload?.action)
  const result = asRecord(payload?.result)
  const evidence = asRecord(result?.evidence)
  if (!event || (!action && !result && !evidence)) {
    return []
  }

  return [
    getString(action?.kind || action?.type)
      ? `Action: ${humanizeIdentifier(getString(action?.kind || action?.type))}`
      : '',
    getString(action?.target) ? `Target: ${truncateText(getString(action?.target), 60)}` : '',
    getString(action?.ref || evidence?.ref) ? `Ref: ${getString(action?.ref || evidence?.ref)}` : '',
    getString(evidence?.role) ? `Role: ${humanizeIdentifier(getString(evidence?.role))}` : '',
    getFiniteNumber(evidence?.matched_count) !== null
      ? `Matches: ${getFiniteNumber(evidence?.matched_count)}`
      : '',
    getString(evidence?.covered_by)
      ? `Covered by: ${truncateText(getString(evidence?.covered_by), 60)}`
      : '',
    formatEvidenceRect(asRecord(evidence?.rect)),
    getString(result?.reason) ? `Reason: ${humanizeIdentifier(getString(result?.reason))}` : '',
    getString(result?.message) ? `Message: ${truncateText(getString(result?.message), 90)}` : ''
  ].filter(Boolean)
}

function redactBrowserUrlForDisplay(value: string) {
  if (!value) {
    return ''
  }

  try {
    const url = new URL(value)
    url.username = ''
    url.password = ''
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return ''
  }
}

function getBrowserSensitiveState(snapshot: ResultRecord | null) {
  const sensitive = asRecord(snapshot?.sensitive)
  if (!sensitive) {
    return ''
  }

  const count =
    Number(getFiniteNumber(sensitive.password_fields || sensitive.passwordFields) || 0) +
    Number(getFiniteNumber(sensitive.mfa_fields || sensitive.mfaFields) || 0) +
    Number(getFiniteNumber(sensitive.sensitive_controls || sensitive.sensitiveControls) || 0)

  return count > 0 || sensitive.login_like === true || sensitive.loginLike === true
    ? 'Sensitive fields visible'
    : ''
}

function formatEvidenceRect(rect: ResultRecord | null) {
  if (!rect) {
    return ''
  }

  const x = getFiniteNumber(rect.x)
  const y = getFiniteNumber(rect.y)
  const width = getFiniteNumber(rect.width)
  const height = getFiniteNumber(rect.height)
  if (x === null || y === null || width === null || height === null) {
    return ''
  }

  return `Rect: ${Math.round(width)}x${Math.round(height)} at ${Math.round(x)}, ${Math.round(y)}`
}

function buildPreviewSection(
  title: string,
  values: unknown[],
  formatter: (value: unknown) => PreviewItem | null
): PreviewSection | null {
  const items: PreviewItem[] = []

  for (const value of values) {
    const item = formatter(value)
    if (item) {
      items.push(item)
    }
  }

  if (items.length === 0) {
    return null
  }

  return {
    title,
    items: items.slice(0, MAX_PREVIEW_ITEMS),
    remainingCount: Math.max(items.length - MAX_PREVIEW_ITEMS, 0)
  }
}

function buildPreviewSections(result: ResultRecord) {
  const sections: PreviewSection[] = []
  const attachmentValues = getArray(result.attachments).slice()
  if (attachmentValues.length === 0 && result.attachment) {
    attachmentValues.push(result.attachment)
  }
  const resultsSection = buildPreviewSection(
    'Results',
    getArray(result.results),
    buildEntryPreviewItem
  )
  const entriesSection = buildPreviewSection(
    'Entries',
    getArray(result.entries),
    buildEntryPreviewItem
  )
  const childrenSection = buildPreviewSection(
    'Children',
    getArray(result.children),
    buildEntryPreviewItem
  )
  const devicesSection = buildPreviewSection(
    'Devices',
    getArray(result.devices),
    buildDevicePreviewItem
  )
  const attachmentsSection = buildPreviewSection(
    'Attachments',
    attachmentValues,
    buildAttachmentPreviewItem
  )
  const approvedRootsSection = buildPreviewSection(
    'Approved Folders',
    getArray(result.approvedRoots),
    buildRootPreviewItem
  )
  const searchRootsSection = buildPreviewSection(
    'Search Roots',
    getArray(result.searchRoots),
    buildRootPreviewItem
  )
  const appliedEditsSection = buildPreviewSection(
    'Applied Edits',
    getArray(result.appliedEdits),
    buildEditPreviewItem
  )
  const timelineSection = buildPreviewSection(
    'Trace Timeline',
    getArray(result.events),
    buildEventPreviewItem
  )

  if (timelineSection) sections.push(timelineSection)
  if (resultsSection) sections.push(resultsSection)
  if (entriesSection) sections.push(entriesSection)
  if (childrenSection) sections.push(childrenSection)
  if (devicesSection) sections.push(devicesSection)
  if (attachmentsSection) sections.push(attachmentsSection)
  if (approvedRootsSection) sections.push(approvedRootsSection)
  if (searchRootsSection) sections.push(searchRootsSection)
  if (appliedEditsSection) sections.push(appliedEditsSection)

  return sections
}

function buildLatestScreenshotPreview(result: ResultRecord): ScreenshotPreview | null {
  const screenshot = getLatestScreenshotRecord(result)
  if (!screenshot) {
    return null
  }

  const url =
    getString(screenshot.imageUrl) ||
    getString(screenshot.image_url) ||
    getString(screenshot.signed_url) ||
    getString(screenshot.signedUrl) ||
    getString(screenshot.url)
  if (!url) {
    return null
  }

  const width = getFiniteNumber(screenshot.width)
  const height = getFiniteNumber(screenshot.height)
  const dimensions =
    width !== null && height !== null && width > 0 && height > 0 ? `${width}×${height}` : ''
  const label =
    getString(screenshot.name) ||
    getString(screenshot.source_name) ||
    getString(screenshot.sourceName) ||
    'Latest desktop screenshot'
  const meta = [
    dimensions,
    formatDateTime(
      screenshot.capturedAt ||
        screenshot.captured_at ||
        screenshot.createdAt ||
        screenshot.created_at
    ),
    getString(screenshot.activeAppName) || getString(screenshot.active_app_name)
  ].filter(Boolean)
  const context = [
    getString(screenshot.ocrSummary) || getString(screenshot.ocr_summary),
    getString(screenshot.accessibilitySummary) || getString(screenshot.accessibility_summary)
  ]
    .filter(Boolean)
    .map((value) => truncateText(value, 180))

  return {
    url,
    label,
    meta,
    context
  }
}

function buildPendingActionPreview(result: ResultRecord): PendingActionPreview | null {
  const pendingAction = getPendingActionRecord(result)
  if (!pendingAction) {
    return null
  }

  const kind = getString(pendingAction.kind) || getString(pendingAction.type)
  const title =
    getString(pendingAction.title) ||
    getString(pendingAction.label) ||
    [humanizeIdentifier(kind), getString(pendingAction.target)].filter(Boolean).join(' ') ||
    'Pending Action'
  const description =
    getString(pendingAction.summary) ||
    getString(pendingAction.description) ||
    getString(pendingAction.reason) ||
    getString(pendingAction.rationale)
  const requiresApproval =
    pendingAction.requires_approval === true || pendingAction.requiresApproval === true
  const approved = result.pendingActionApproved === true
  const hotkey = getArray(pendingAction.hotkey || pendingAction.keys)
    .map((entry) => getString(entry))
    .filter(Boolean)
    .join(' + ')
  const coordinates = asRecord(pendingAction.coordinates)
  const coordinateX = getFiniteNumber(coordinates?.x)
  const coordinateY = getFiniteNumber(coordinates?.y)
  const scroll = asRecord(pendingAction.scroll)
  const sensitivityReasons = getArray(
    pendingAction.sensitivityReasons || pendingAction.sensitivity_reasons
  )
    .map((entry) => getString(entry))
    .filter(Boolean)
    .slice(0, 4)
  const details = [
    kind ? humanizeIdentifier(kind) : '',
    getString(pendingAction.target) ? `Target: ${getString(pendingAction.target)}` : '',
    hotkey ? `Hotkey: ${hotkey}` : '',
    getString(pendingAction.key) ? `Key: ${humanizeIdentifier(getString(pendingAction.key))}` : '',
    coordinateX !== null && coordinateY !== null ? `Point: ${coordinateX}, ${coordinateY}` : '',
    scroll
      ? `Scroll: ${[getString(scroll.direction), getFiniteNumber(scroll.amount)]
          .filter((value) => value !== null && value !== '')
          .join(' ')}`
      : ''
  ].filter(Boolean)
  let stateLabel = 'Queued'

  if (requiresApproval && !approved) {
    stateLabel = 'Needs Approval'
  } else if (approved) {
    stateLabel = 'Approved'
  } else if (result.planned === true) {
    stateLabel = 'Planned'
  }

  return {
    title,
    description,
    requiresApproval,
    approved,
    stateLabel,
    details: details.slice(0, 4),
    sensitivityReasons
  }
}

function buildDecisionPreview(result: ResultRecord): DecisionPreview | null {
  const decision = getLatestDecisionRecord(result)
  if (!decision) {
    return null
  }

  const statusKey = normalizeIdentifier(getString(decision.status)) || 'continue'
  const normalizedStatus: 'continue' | 'completed' | 'blocked' =
    statusKey === 'completed' || statusKey === 'blocked' ? statusKey : 'continue'
  const statusLabel =
    normalizedStatus === 'completed'
      ? 'Completed'
      : normalizedStatus === 'blocked'
        ? 'Blocked'
        : 'Continue'
  const description =
    getString(decision.completionMessage) ||
    getString(decision.completion_message) ||
    getString(decision.observation)
  const rationale = getString(decision.rationale)
  const reasoningSummary =
    getString(decision.reasoningSummary) || getString(decision.reasoning_summary)
  const turnId = shortenId(decision.turnId || decision.turn_id)
  const responseId = shortenId(decision.responseId || decision.response_id)
  const meta = [turnId ? `Turn ${turnId}` : '', responseId ? `Response ${responseId}` : ''].filter(
    Boolean
  )

  return {
    statusKey: normalizedStatus,
    statusLabel,
    description,
    rationale,
    reasoningSummary,
    meta,
    badgeVariant:
      normalizedStatus === 'blocked'
        ? 'destructive'
        : normalizedStatus === 'completed'
          ? 'secondary'
          : 'outline'
  }
}

function buildComputerControlStatePreview(
  result: ResultRecord
): ComputerControlStatePreview | null {
  const sessionStatus = normalizeIdentifier(getSessionStatus(result))
  const pendingAction = buildPendingActionPreview(result)
  const latestDecision = buildDecisionPreview(result)
  const latestScreenshot = buildLatestScreenshotPreview(result)
  const awaitingApproval =
    result.awaitingApproval === true ||
    sessionStatus === 'awaiting_user_approval' ||
    !!(pendingAction?.requiresApproval && !pendingAction.approved)
  const detailBadges = [
    result.planned === true ? 'Planner ready' : '',
    result.canAdvance === true ? 'Can advance' : '',
    result.requiresHumanAction === true ? 'Human action required' : '',
    getString(result.mode) ? humanizeIdentifier(getString(result.mode)) : ''
  ].filter(Boolean)

  if (awaitingApproval) {
    return {
      title: 'Action needs approval',
      description: pendingAction
        ? `Review ${pendingAction.title} before the desktop session can continue.`
        : 'A desktop action is waiting for human approval before the session can continue.',
      badgeLabel: 'Awaiting Approval',
      badgeVariant: 'outline',
      containerClassName:
        'border-amber-300/50 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10',
      detailBadges
    }
  }

  if (latestDecision?.statusKey === 'blocked') {
    return {
      title: 'Planner is blocked',
      description:
        latestDecision.description ||
        latestDecision.rationale ||
        'The planner could not safely choose the next step from the latest screenshot.',
      badgeLabel: 'Blocked',
      badgeVariant: 'destructive',
      containerClassName: 'border-destructive/40 bg-destructive/5',
      detailBadges
    }
  }

  if (latestDecision?.statusKey === 'completed' || sessionStatus === 'completed') {
    return {
      title: 'Goal completed',
      description:
        latestDecision?.description || 'The planner marked this desktop goal as complete.',
      badgeLabel: 'Completed',
      badgeVariant: 'secondary',
      containerClassName: 'border-primary/25 bg-primary/5',
      detailBadges
    }
  }

  if (pendingAction && (result.planned === true || result.pendingActionApproved === true)) {
    return {
      title: pendingAction.approved ? 'Action approved' : 'Next action planned',
      description:
        pendingAction.description ||
        `The planner is ready to ${pendingAction.title.toLowerCase()}.`,
      badgeLabel: pendingAction.stateLabel,
      badgeVariant: pendingAction.approved ? 'secondary' : 'outline',
      containerClassName: 'border-primary/25 bg-primary/5',
      detailBadges
    }
  }

  if (sessionStatus === 'awaiting_desktop') {
    return {
      title: 'Waiting for desktop',
      description:
        'The session is waiting for the desktop device to capture or return the next state.',
      badgeLabel: 'Waiting',
      badgeVariant: 'outline',
      containerClassName: 'border-muted-foreground/20 bg-muted/30',
      detailBadges
    }
  }

  if (sessionStatus === 'created') {
    return {
      title: 'Session created',
      description:
        'The first screenshot has not been captured yet for this desktop control session.',
      badgeLabel: 'Planned',
      badgeVariant: 'outline',
      containerClassName: 'border-muted-foreground/20 bg-muted/30',
      detailBadges
    }
  }

  if (sessionStatus === 'paused') {
    return {
      title: 'Session paused',
      description: 'Resume or approve the next action when you want planning to continue.',
      badgeLabel: 'Paused',
      badgeVariant: 'outline',
      containerClassName: 'border-muted-foreground/20 bg-muted/30',
      detailBadges
    }
  }

  if (sessionStatus === 'running' || latestScreenshot) {
    return {
      title: 'Session running',
      description: latestScreenshot
        ? 'The latest desktop screenshot is available and the planner can use it for the next step.'
        : 'The desktop session is actively collecting context for the next step.',
      badgeLabel: 'Running',
      badgeVariant: 'secondary',
      containerClassName: 'border-primary/25 bg-primary/5',
      detailBadges
    }
  }

  return null
}

function getStatusTone(success: boolean, sessionStatus: string): StatusTone {
  const normalizedSessionStatus = normalizeIdentifier(sessionStatus)
  const isFailure =
    !success || normalizedSessionStatus === 'failed' || normalizedSessionStatus === 'cancelled'

  if (isFailure) {
    return {
      success: false,
      statusLabel: normalizedSessionStatus ? humanizeIdentifier(sessionStatus) : 'Failed',
      containerClassName: 'border-destructive/40 bg-destructive/5',
      iconClassName: 'bg-destructive/10 text-destructive',
      badgeVariant: 'destructive'
    }
  }

  if (normalizedSessionStatus === 'completed') {
    return {
      success: true,
      statusLabel: 'Completed',
      containerClassName: 'border-primary/25 bg-primary/5',
      iconClassName: 'bg-primary/10 text-primary',
      badgeVariant: 'secondary'
    }
  }

  if (normalizedSessionStatus === 'awaiting_user_approval') {
    return {
      success: true,
      statusLabel: 'Awaiting Approval',
      containerClassName:
        'border-amber-300/50 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10',
      iconClassName: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
      badgeVariant: 'outline'
    }
  }

  if (normalizedSessionStatus === 'paused') {
    return {
      success: true,
      statusLabel: 'Paused',
      containerClassName: 'border-muted-foreground/20 bg-muted/30',
      iconClassName: 'bg-muted text-foreground/80',
      badgeVariant: 'outline'
    }
  }

  if (normalizedSessionStatus === 'running') {
    return {
      success: true,
      statusLabel: 'Running',
      containerClassName: 'border-primary/25 bg-primary/5',
      iconClassName: 'bg-primary/10 text-primary',
      badgeVariant: 'secondary'
    }
  }

  if (normalizedSessionStatus === 'awaiting_desktop') {
    return {
      success: true,
      statusLabel: 'Waiting for Desktop',
      containerClassName: 'border-muted-foreground/20 bg-muted/30',
      iconClassName: 'bg-muted text-foreground/80',
      badgeVariant: 'outline'
    }
  }

  if (normalizedSessionStatus === 'created') {
    return {
      success: true,
      statusLabel: 'Planned',
      containerClassName: 'border-muted-foreground/20 bg-muted/30',
      iconClassName: 'bg-muted text-foreground/80',
      badgeVariant: 'outline'
    }
  }

  return {
    success: true,
    statusLabel: 'Completed',
    containerClassName: 'border-primary/25 bg-primary/5',
    iconClassName: 'bg-primary/10 text-primary',
    badgeVariant: 'secondary'
  }
}

function buildContentPreview(result: ResultRecord) {
  const contentCandidates = [
    typeof result.content === 'string' ? result.content : '',
    typeof result.text === 'string' ? result.text : ''
  ]

  const directContent = contentCandidates.find((value) => value.trim())
  if (directContent) {
    return truncateText(directContent, CONTENT_PREVIEW_LENGTH)
  }

  const stdout = typeof result.stdout === 'string' ? result.stdout.trim() : ''
  const stderr = typeof result.stderr === 'string' ? result.stderr.trim() : ''
  const commandOutput = [stdout ? `stdout\n${stdout}` : '', stderr ? `stderr\n${stderr}` : '']
    .filter(Boolean)
    .join('\n\n')

  return commandOutput ? truncateText(commandOutput, CONTENT_PREVIEW_LENGTH) : ''
}

function useBrowserSidecarLiveSession(sessionId: string, enabled: boolean) {
  const [liveResult, setLiveResult] = useState<ResultRecord | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSendingControl, setIsSendingControl] = useState(false)
  const [controlMessage, setControlMessage] = useState('')
  const [controlError, setControlError] = useState('')
  const requestIdRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      requestIdRef.current += 1
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!enabled || !sessionId) {
      return null
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setIsRefreshing(true)
    try {
      const response = await fetch(`/api/v1/desktop/agent-sessions/${sessionId}?limit=100`, {
        cache: 'no-store'
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(getString(json?.error) || 'Could not refresh Browser Sidecar status')
      }

      const nextResult = asRecord(json)
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLiveResult(nextResult)
        setControlError('')
      }
      return nextResult
    } catch (error) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setControlError(
          error instanceof Error ? error.message : 'Could not refresh Browser Sidecar'
        )
      }
      return null
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setIsRefreshing(false)
      }
    }
  }, [enabled, sessionId])

  const postControl = useCallback(
    async (kind: 'approve' | 'resume' | 'cancel') => {
      if (!enabled || !sessionId) {
        return
      }

      const endpoint =
        kind === 'approve'
          ? 'approve'
          : kind === 'resume'
            ? 'resume'
            : 'cancel'
      const body =
        kind === 'cancel'
          ? { reason: 'Browser Sidecar cancelled from Losi.' }
          : { summary: `Browser Sidecar ${kind} sent from Losi.` }

      setIsSendingControl(true)
      setControlError('')
      setControlMessage('')
      try {
        const response = await fetch(`/api/v1/desktop/agent-sessions/${sessionId}/${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        })
        const json = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(getString(json?.error) || `Could not ${kind} Browser Sidecar`)
        }

        const nextResult = asRecord(json)
        setLiveResult(nextResult)
        setControlMessage(
          kind === 'cancel'
            ? 'Cancelled'
            : kind === 'approve'
              ? 'Approved'
              : 'Resume sent'
        )
      } catch (error) {
        setControlError(error instanceof Error ? error.message : `Could not ${kind} Browser Sidecar`)
      } finally {
        setIsSendingControl(false)
      }
    },
    [enabled, sessionId]
  )

  useEffect(() => {
    setLiveResult(null)
    setControlMessage('')
    setControlError('')
  }, [sessionId])

  useEffect(() => {
    if (!enabled || !sessionId) {
      return
    }

    let cancelled = false
    let timeoutId: number | null = null
    const stopPolling = () => {
      cancelled = true
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
    }
    const schedulePoll = () => {
      if (!cancelled) {
        timeoutId = window.setTimeout(poll, BROWSER_SIDECAR_LIVE_POLL_MS)
      }
    }
    const poll = async () => {
      if (cancelled) {
        return
      }

      const nextResult = await refresh()
      const nextStatus = getSessionStatus(nextResult)
      if (nextStatus && isTerminalSessionStatus(nextStatus)) {
        stopPolling()
        return
      }

      schedulePoll()
    }

    poll()

    return stopPolling
  }, [enabled, refresh, sessionId])

  return {
    liveResult,
    isRefreshing,
    isSendingControl,
    controlMessage,
    controlError,
    refresh,
    approve: () => postControl('approve'),
    resume: () => postControl('resume'),
    cancel: () => postControl('cancel')
  }
}

type BrowserSidecarLivePanelProps = {
  result: ResultRecord
  sessionId: string
  isRefreshing: boolean
  isSendingControl: boolean
  controlMessage: string
  controlError: string
  onRefresh: () => void
  onApprove: () => void
  onResume: () => void
  onCancel: () => void
}

function BrowserSidecarLivePanel({
  result,
  sessionId,
  isRefreshing,
  isSendingControl,
  controlMessage,
  controlError,
  onRefresh,
  onApprove,
  onResume,
  onCancel
}: BrowserSidecarLivePanelProps) {
  const sessionStatus = getSessionStatus(result)
  const normalizedStatus = normalizeIdentifier(sessionStatus)
  const pendingAction = buildPendingActionPreview(result)
  const browserSnapshot = getBrowserSnapshotRecord(result)
  const events = getEventRecords(result).slice(-5).reverse()
  const terminal = isTerminalSessionStatus(sessionStatus)
  const needsApproval = !!pendingAction?.requiresApproval && !pendingAction.approved
  const canResume = !terminal && normalizedStatus === 'paused'
  const canCancel = !terminal
  const currentUrl = getString(browserSnapshot?.url)
  const safeCurrentUrl = redactBrowserUrlForDisplay(currentUrl)
  const currentTitle = getString(browserSnapshot?.title)
  const sensitiveState = getBrowserSensitiveState(browserSnapshot)

  return (
    <div className="ph-no-capture border-primary/25 bg-background/55 space-y-2 rounded-md border p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-foreground/90 text-xs font-medium">Live Browser Sidecar</p>
          <p className="text-muted-foreground text-[11px]">
            {sessionStatus ? humanizeIdentifier(sessionStatus) : 'Checking'} • {shortenId(sessionId)}
          </p>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-1.5">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-7"
            disabled={isRefreshing || isSendingControl}
            onClick={onRefresh}
            aria-label="Refresh Browser Sidecar status"
          >
            <RefreshCw className={cn('size-3.5', isRefreshing && 'animate-spin')} />
          </Button>
          {needsApproval && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 gap-1 px-2 text-[11px]"
              disabled={isSendingControl}
              onClick={onApprove}
            >
              <Check className="size-3" />
              Approve and continue
            </Button>
          )}
          {canResume && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 gap-1 px-2 text-[11px]"
              disabled={isSendingControl}
              onClick={onResume}
            >
              <Play className="size-3" />
              Resume
            </Button>
          )}
          {canCancel && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-muted-foreground h-7 gap-1 px-2 text-[11px]"
              disabled={isSendingControl}
              onClick={onCancel}
            >
              <XCircle className="size-3" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="border-border/50 bg-background/50 rounded-md border p-2">
        <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
          Current Page
        </p>
        {currentTitle || safeCurrentUrl ? (
          <div className="mt-1 space-y-0.5">
            {currentTitle && (
              <p className="text-xs font-medium">{truncateText(currentTitle, 120)}</p>
            )}
            {safeCurrentUrl && (
              <p className="text-muted-foreground break-all font-mono text-[11px]">
                {truncateText(safeCurrentUrl, 220)}
              </p>
            )}
            {sensitiveState && (
              <Badge variant="outline" className="mt-1 text-[10px]">
                {sensitiveState}
              </Badge>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground mt-1 text-xs">Waiting for browser observation.</p>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
          Recent Events
        </p>
        {events.length > 0 ? (
          events.map((event) => {
            const item = buildEventPreviewItem(event)
            const evidence = buildBrowserSidecarEventEvidence(event)
            if (!item) {
              return null
            }

            return (
              <div
                key={`${item.meta || ''}-${item.primary}-${item.secondary || ''}`}
                className="border-border/40 bg-background/40 flex items-start justify-between gap-3 rounded-md border p-2"
              >
                <div className="min-w-0">
                  <p className="text-xs font-medium">{truncateText(item.primary, 140)}</p>
                  {item.secondary && (
                    <p className="text-muted-foreground text-[11px]">{item.secondary}</p>
                  )}
                  {evidence.length > 0 && (
                    <details className="mt-1.5">
                      <summary className="text-muted-foreground cursor-pointer text-[11px] font-medium">
                        Evidence
                      </summary>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {evidence.map((detail) => (
                          <Badge
                            key={detail}
                            variant="outline"
                            className="max-w-full truncate text-[10px]"
                          >
                            {detail}
                          </Badge>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
                {item.meta && (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {item.meta}
                  </Badge>
                )}
              </div>
            )
          })
        ) : (
          <p className="border-border/40 bg-background/40 text-muted-foreground rounded-md border p-2 text-xs">
            Waiting for the first Browser Sidecar event.
          </p>
        )}
      </div>

      {(controlMessage || controlError) && (
        <p className={cn('text-[11px]', controlError ? 'text-destructive' : 'text-primary')}>
          {controlError || controlMessage}
        </p>
      )}
    </div>
  )
}

const DesktopToolAnnotation = memo(({ annotation, toolKey }: DesktopToolAnnotationProps) => {
  const rawValue = (annotation as Record<string, unknown>)?.[toolKey]
  const toolMeta = getToolMeta(toolKey)
  const resultValue = unwrapToolAnnotationResult(rawValue)
  const result = asRecord(resultValue)
  const browserSidecarSessionId =
    toolMeta?.toolName === 'BrowserExtensionHandoffTool' ? getResultSessionId(result) : ''
  const browserSidecarLive = useBrowserSidecarLiveSession(
    browserSidecarSessionId,
    !!toolMeta && !!browserSidecarSessionId
  )
  const effectiveResult = useMemo(
    () => mergeLiveSessionResult(result, browserSidecarLive.liveResult),
    [browserSidecarLive.liveResult, result]
  )

  if (rawValue == null || !toolMeta) {
    return null
  }

  const actionKey = getActionKey(toolKey, effectiveResult)
  const actionLabel = getActionLabel(actionKey)
  const errorMessage = effectiveResult ? getString(effectiveResult.error) : ''
  const exitCode = effectiveResult ? getFiniteNumber(effectiveResult.exitCode) : null
  const success = effectiveResult
    ? effectiveResult.success !== false && !errorMessage && (exitCode === null || exitCode === 0)
    : true
  const summary = effectiveResult
    ? buildSummary(effectiveResult, actionKey, toolMeta.label, toolMeta.toolName)
    : ''
  const warning = effectiveResult
    ? getString(effectiveResult.warning) || getString(effectiveResult.plannerError)
    : ''
  const badges = effectiveResult ? buildDetailBadges(effectiveResult, actionKey) : []
  const pathFields = effectiveResult ? buildPathFields(effectiveResult) : []
  const factFields = effectiveResult ? buildFactFields(effectiveResult, toolMeta.toolName) : []
  const previewSections = effectiveResult ? buildPreviewSections(effectiveResult) : []
  const latestScreenshot = effectiveResult ? buildLatestScreenshotPreview(effectiveResult) : null
  const pendingAction = effectiveResult ? buildPendingActionPreview(effectiveResult) : null
  const latestDecision = effectiveResult ? buildDecisionPreview(effectiveResult) : null
  const computerControlState =
    effectiveResult && isComputerControlResult(toolMeta.toolName, effectiveResult)
      ? buildComputerControlStatePreview(effectiveResult)
      : null
  const sessionStatus = effectiveResult ? getSessionStatus(effectiveResult) : ''
  const contentPreview = effectiveResult
    ? buildContentPreview(effectiveResult)
    : typeof resultValue === 'string'
      ? truncateText(resultValue, CONTENT_PREVIEW_LENGTH)
      : ''
  const ToolIcon = toolMeta.icon
  const statusTone = getStatusTone(success, sessionStatus)
  const StatusIcon = statusTone.success ? CheckCircle2 : XCircle

  return (
    <Card className={cn('p-3', statusTone.containerClassName)}>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            statusTone.iconClassName
          )}
        >
          <ToolIcon className="size-4" />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{actionLabel}</span>
              <Badge variant="outline" className="text-[11px]">
                {toolMeta.label}
              </Badge>
              <Badge
                variant={statusTone.badgeVariant}
                className="inline-flex items-center gap-1 text-[11px]"
              >
                <StatusIcon className="size-3" />
                {statusTone.statusLabel}
              </Badge>
            </div>

            {(errorMessage || summary) && (
              <p className={cn('text-xs', success ? 'text-muted-foreground' : 'text-destructive')}>
                {errorMessage || summary}
              </p>
            )}

            {warning && success && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400">{warning}</p>
            )}
          </div>

          {computerControlState && (
            <div
              className={cn(
                'space-y-2 rounded-md border p-2.5',
                computerControlState.containerClassName
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-foreground/90 text-xs font-medium">
                    {computerControlState.title}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {computerControlState.description}
                  </p>
                </div>
                <Badge variant={computerControlState.badgeVariant} className="shrink-0 text-[10px]">
                  {computerControlState.badgeLabel}
                </Badge>
              </div>

              {computerControlState.detailBadges.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {computerControlState.detailBadges.map((detail) => (
                    <Badge key={detail} variant="outline" className="text-[10px]">
                      {detail}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {effectiveResult && browserSidecarSessionId && (
            <BrowserSidecarLivePanel
              result={effectiveResult}
              sessionId={browserSidecarSessionId}
              isRefreshing={browserSidecarLive.isRefreshing}
              isSendingControl={browserSidecarLive.isSendingControl}
              controlMessage={browserSidecarLive.controlMessage}
              controlError={browserSidecarLive.controlError}
              onRefresh={browserSidecarLive.refresh}
              onApprove={browserSidecarLive.approve}
              onResume={browserSidecarLive.resume}
              onCancel={browserSidecarLive.cancel}
            />
          )}

          {pendingAction && (
            <div className="border-border/50 bg-background/40 space-y-2 rounded-md border p-2.5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-foreground/90 text-xs font-medium">Planned Action</p>
                <Badge
                  variant={
                    pendingAction.requiresApproval && !pendingAction.approved
                      ? 'outline'
                      : 'secondary'
                  }
                  className="text-[10px]"
                >
                  {pendingAction.stateLabel}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">{pendingAction.title}</p>
                {pendingAction.description && (
                  <p className="text-muted-foreground text-xs">{pendingAction.description}</p>
                )}
              </div>
              {pendingAction.details.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {pendingAction.details.map((detail) => (
                    <Badge key={detail} variant="outline" className="text-[10px]">
                      {detail}
                    </Badge>
                  ))}
                </div>
              )}
              {pendingAction.sensitivityReasons.length > 0 && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                    Approval Context
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {pendingAction.sensitivityReasons.map((reason) => (
                      <Badge key={reason} variant="outline" className="text-[10px]">
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {latestDecision && (
            <div className="border-border/50 bg-background/40 space-y-2 rounded-md border p-2.5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-foreground/90 text-xs font-medium">Latest Decision</p>
                <Badge variant={latestDecision.badgeVariant} className="text-[10px]">
                  {latestDecision.statusLabel}
                </Badge>
              </div>
              <div className="space-y-1">
                {latestDecision.description && (
                  <p className="text-sm font-medium">
                    {truncateText(latestDecision.description, 220)}
                  </p>
                )}
                {latestDecision.rationale && (
                  <p className="text-muted-foreground text-xs">
                    <span className="text-foreground/80 font-medium">Rationale:</span>{' '}
                    {truncateText(latestDecision.rationale, 240)}
                  </p>
                )}
                {latestDecision.reasoningSummary && (
                  <p className="text-muted-foreground text-xs">
                    <span className="text-foreground/80 font-medium">Context:</span>{' '}
                    {truncateText(latestDecision.reasoningSummary, 240)}
                  </p>
                )}
              </div>
              {latestDecision.meta.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {latestDecision.meta.map((detail) => (
                    <Badge key={detail} variant="outline" className="text-[10px]">
                      {detail}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {latestScreenshot && (
            <div className="border-border/50 bg-background/50 space-y-2 rounded-md border p-2.5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-foreground/90 text-xs font-medium">Latest Screenshot</p>
                {latestScreenshot.meta.length > 0 && (
                  <p className="text-muted-foreground text-[11px]">
                    {latestScreenshot.meta.join(' • ')}
                  </p>
                )}
              </div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={latestScreenshot.url}
                alt={latestScreenshot.label}
                className="border-border/50 bg-muted/20 max-h-72 w-full rounded-md border object-contain"
                loading="lazy"
              />
              {latestScreenshot.context.length > 0 && (
                <div className="space-y-1">
                  {latestScreenshot.context.map((contextLine, index) => (
                    <p key={`${contextLine}-${index}`} className="text-muted-foreground text-xs">
                      {contextLine}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {badges.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {badges.map((badge) => (
                <Badge
                  key={`${badge.label}-${badge.variant}`}
                  variant={badge.variant}
                  className="text-[11px]"
                >
                  {badge.label}
                </Badge>
              ))}
            </div>
          )}

          {pathFields.length > 0 && (
            <div className="border-border/50 bg-background/40 space-y-2 rounded-md border p-2.5">
              {pathFields.map((pathField) => (
                <div key={`${pathField.label}-${pathField.value}`} className="space-y-1">
                  <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                    {pathField.label}
                  </p>
                  <p className="text-foreground/90 break-all font-mono text-xs">
                    {pathField.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {factFields.length > 0 && (
            <dl className="grid gap-2 sm:grid-cols-2">
              {factFields.map((factField) => (
                <div
                  key={`${factField.label}-${factField.value}`}
                  className="border-border/50 bg-background/40 rounded-md border p-2.5"
                >
                  <dt className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                    {factField.label}
                  </dt>
                  <dd className="text-foreground/90 mt-1 text-xs">{factField.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {previewSections.length > 0 && (
            <div className="space-y-2">
              {previewSections.map((section) => (
                <div
                  key={section.title}
                  className="border-border/50 bg-background/40 rounded-md border p-2.5"
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-foreground/90 text-xs font-medium">{section.title}</p>
                    {section.remainingCount > 0 && (
                      <p className="text-muted-foreground text-[11px]">
                        +{section.remainingCount} more
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    {section.items.map((item, index) => (
                      <div
                        key={`${section.title}-${item.primary}-${index}`}
                        className="flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{item.primary}</p>
                          {item.secondary && (
                            <p className="text-muted-foreground break-all text-xs">
                              {item.secondary}
                            </p>
                          )}
                        </div>

                        {item.meta && (
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            {item.meta}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {contentPreview && (
            <div className="border-border/50 bg-background/40 rounded-md border p-2.5">
              <p className="text-foreground/90 mb-2 text-xs font-medium">Content Preview</p>
              <pre className="text-muted-foreground whitespace-pre-wrap break-words font-mono text-xs">
                {contentPreview}
              </pre>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
})

DesktopToolAnnotation.displayName = 'DesktopToolAnnotation'

export default DesktopToolAnnotation
